import React from 'react';
import { T, FONT_STACK } from '../customer/customers.theme';

/**
 * The menu under the avatar.
 *
 * It used to be a Material-UI `Menu` with its own shadow, its own type scale
 * and its own hover — the only thing on the app wearing that kit, which is why
 * it read as a piece of somebody else's program sitting on top of this one.
 * This is the same popover the rest of the till uses: our card, our border,
 * our ink, and the row treatment from the printer and category lists.
 *
 * ── On the positioning ─────────────────────────────────────────────────────
 *
 * Absolute, inside the avatar's own wrapper, which is what pins it directly
 * under the avatar. An earlier version measured the avatar and placed the card
 * with `position: fixed` and viewport coordinates; that is the fragile way to
 * do it, because a `fixed` child is positioned against the nearest ancestor
 * carrying a transform or a filter rather than the viewport, and the app bar
 * is full of styled wrappers. Hanging it off the anchor cannot drift.
 *
 * The app bar already sits above the page (`zIndex: drawer + 1`) and clips
 * nothing, so the card is free to overflow below it.
 */

const base = (size) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.9,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
});

export const PencilIcon = ({ size = 16 }) => (
  <svg {...base(size)}>
    <path d="M14.5 4.5l5 5L9 20H4v-5z" />
    <path d="M12.5 6.5l5 5" />
  </svg>
);

export const LockIcon = ({ size = 16 }) => (
  <svg {...base(size)}>
    <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
    <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
  </svg>
);

export const SignOutIcon = ({ size = 16 }) => (
  <svg {...base(size)}>
    <path d="M14.5 4.5H6a1.5 1.5 0 0 0-1.5 1.5v12A1.5 1.5 0 0 0 6 19.5h8.5" />
    <path d="M16 8.5l3.5 3.5-3.5 3.5M19 12H9.5" />
  </svg>
);

export default function AccountMenu({ anchorRef, items, onClose }) {
  const menuRef = React.useRef(null);

  // Escape and an outside click dismiss it. The anchor is excluded so clicking
  // the avatar again closes rather than immediately reopening.
  React.useEffect(() => {
    const onPointerDown = (event) => {
      const inMenu = menuRef.current && menuRef.current.contains(event.target);
      const inAnchor =
        anchorRef && anchorRef.current && anchorRef.current.contains(event.target);
      if (!inMenu && !inAnchor) onClose();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose, anchorRef]);

  return (
    <div
      ref={menuRef}
      role="menu"
      style={{
        position: 'absolute',
        top: 'calc(100% + 8px)',
        right: 0,
        zIndex: 1400,
        minWidth: 210,
        padding: 6,
        background: T.surface,
        border: `1px solid ${T.border}`,
        borderRadius: T.radiusPanel,
        boxShadow: T.dialog,
        fontFamily: FONT_STACK,
      }}
    >
      {items.map((item, index) =>
        item.divider ? (
          <div
            key={`divider-${index}`}
            style={{ height: 1, background: T.hairline, margin: '5px 8px' }}
          />
        ) : (
          <MenuRow key={item.label} item={item} onClose={onClose} />
        )
      )}
    </div>
  );
}

function MenuRow({ item, onClose }) {
  const [hovered, setHovered] = React.useState(false);
  const danger = Boolean(item.danger);

  return (
    <button
      type="button"
      role="menuitem"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => {
        // Closed first: an action that opens a dialog must not leave this
        // card sitting over it.
        onClose();
        item.onClick();
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        width: '100%',
        padding: '9px 11px',
        background: hovered ? (danger ? T.dangerTint : T.hover) : 'transparent',
        border: 'none',
        borderRadius: T.radiusControl,
        font: 'inherit',
        fontFamily: FONT_STACK,
        fontSize: 13.5,
        fontWeight: 500,
        textAlign: 'left',
        whiteSpace: 'nowrap',
        color: danger ? T.danger : T.ink,
        cursor: 'pointer',
      }}
    >
      <span
        style={{
          display: 'flex',
          color: danger ? T.danger : hovered ? T.ink : T.inkFaint,
        }}
      >
        {item.icon}
      </span>
      <span>{item.label}</span>
    </button>
  );
}
